import { useEffect, useState } from 'react';

import { FAIconName, useToastMessage } from '@equip.health/ui';
import isNil from 'lodash/isNil';

import { RenderMode } from '~/components/common/billingPeriods/types';
import { BILLING_PERIODS_TOP_LEVEL_ID } from '~/lib/constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  useCreateBillingPeriodMutation,
  useDeleteBillingPeriodMutation,
  useLazyGetBillingPeriodByIdQuery,
  useUpdateBillingPeriodV2Mutation,
} from '~/store/services/clinicalData';

import AccordionFooterButtons from './AccordionFooterButtons';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { UseBillingPeriodState } from './useBillingPeriod';

interface BillingPeriodAccordionFooterProps {
  billingPeriodState: UseBillingPeriodState;
}

const BillingPeriodAccordionFooter = ({
  billingPeriodState,
}: BillingPeriodAccordionFooterProps) => {
  const {
    cancelUpdateBillingPeriodState,
    renderMode,
    resetBillingPeriodState,
    setRenderMode,
    setIsSaveBillingPeriodError,
    oldBillingPeriod,
    updatedBillingPeriod,
    updateBillingPeriodState,
    setOldBillingPeriod,
  } = billingPeriodState;

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  const [createBillingPeriod, { isLoading: isCreateBillingPeriodLoading }] =
    useCreateBillingPeriodMutation();
  const [updateBillingPeriod, { isLoading: isUpdateBillingPeriodLoading }] =
    useUpdateBillingPeriodV2Mutation();
  const [deleteBillingPeriod, { isLoading: isDeleteBillingPeriodLoading }] =
    useDeleteBillingPeriodMutation();

  const [getBillingPeriodById] = useLazyGetBillingPeriodByIdQuery();

  const { setComponentVisibility } = useComponentPortalContext();
  const { currentPatientId } = useGlobalContext();
  const { createToast } = useToastMessage();

  const [currentBillingPeriodId, setCurrentBillingPeriodId] =
    useState<string>();

  const isViewMode = renderMode === RenderMode.VIEW;
  const isEditMode = renderMode === RenderMode.EDIT;
  const isCreateMode = renderMode === RenderMode.CREATE;

  const isRequiredFieldsEmpty =
    !updatedBillingPeriod?.coverageExternalId ||
    !updatedBillingPeriod?.startDate ||
    !updatedBillingPeriod?.equipPayorExternalId ||
    !updatedBillingPeriod?.payorProgramExternalId ||
    !currentPatientId;

  const authsWithEmptyStatus =
    updatedBillingPeriod?.authorizationDetails?.filter(({ status }) =>
      isNil(status),
    );

  const isPrimaryButtonDisabled =
    authsWithEmptyStatus?.length > 0 ||
    isRequiredFieldsEmpty ||
    isCreateBillingPeriodLoading ||
    isUpdateBillingPeriodLoading;

  useEffect(() => {
    if (currentBillingPeriodId) {
      getBillingPeriodById(currentBillingPeriodId)
        .unwrap()
        .then(({ data }) => {
          updateBillingPeriodState(data);
        });
    }
  }, [
    currentBillingPeriodId,
    getBillingPeriodById,
    isCreateMode,
    isEditMode,
    updateBillingPeriodState,
  ]);

  const closeAccordion = () => {
    if (isCreateMode) {
      setComponentVisibility({
        component: PortalComponent.BILLING_PERIOD_ACCORDION,
        data: null,
        isExpanded: false,
        isVisible: false,
      });
      resetBillingPeriodState();
    } else {
      cancelUpdateBillingPeriodState(oldBillingPeriod);
      setRenderMode(RenderMode.VIEW);
    }
  };

  const handleCreateBillingPeriod = () => {
    createBillingPeriod({
      ...updatedBillingPeriod,
      patientExternalId: currentPatientId,
    })
      .unwrap()
      .then(({ data }) => {
        const { billingPeriodId } = data;
        setCurrentBillingPeriodId(billingPeriodId);
        createToast({
          id: 'save-billing-period_toast',
          text: 'Billing period saved.',
          variant: 'success',
          icon: FAIconName.checkRegular,
        });

        setRenderMode(RenderMode.VIEW);
      })
      .catch(() => {
        setIsSaveBillingPeriodError(true);
      });
  };

  const handleUpdateBillingPeriod = () => {
    const { billingPeriodId } = updatedBillingPeriod;

    updateBillingPeriod({
      ...updatedBillingPeriod,
      patientExternalId: currentPatientId,
    })
      .unwrap()
      .then(() => {
        setCurrentBillingPeriodId(billingPeriodId);
        createToast({
          icon: FAIconName.checkRegular,
          id: 'update-billing-period_toast',
          text: 'Billing period updated.',
          variant: 'success',
        });
        setRenderMode(RenderMode.VIEW);
      })
      .catch(() => {
        setIsSaveBillingPeriodError(true);
      });
  };

  const handleDeleteBillingPeriod = (deleteReason: string) => {
    const { billingPeriodId, patientExternalId } = updatedBillingPeriod;
    deleteBillingPeriod({
      billingPeriodId,
      deleteReasonCode: deleteReason,
      patientExternalId,
    })
      .unwrap()
      .then(() => {
        setComponentVisibility({
          component: PortalComponent.BILLING_PERIOD_ACCORDION,
          data: null,
          isExpanded: false,
          isVisible: false,
        });
        setIsDeleteConfirmationModalOpen(false);
        createToast({
          icon: FAIconName.checkRegular,
          id: 'delete-billing-period_toast',
          text: 'Billing period deleted.',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          id: 'delete-billing-period_toast',
          text: 'We’re sorry, we were unable to delete the billing period at this time. Please try again.',
          variant: 'critical',
          icon: FAIconName.exclamationTriangleRegular,
        });
      });
  };

  const toggleBillingPeriodEditMode = () => {
    setOldBillingPeriod(updatedBillingPeriod);
    setRenderMode(RenderMode.EDIT);
  };

  return (
    <>
      {isViewMode && (
        <AccordionFooterButtons
          id={BILLING_PERIODS_TOP_LEVEL_ID}
          onPrimaryButtonClick={toggleBillingPeriodEditMode}
          onSecondaryButtonClick={() => setIsDeleteConfirmationModalOpen(true)}
          primaryButtonTittle="Edit"
          primaryIconName={FAIconName.penRegular}
          secondaryButtonTitle="Delete"
          secondaryIconName={FAIconName.trashRegular}
        />
      )}
      {!isViewMode && (
        <AccordionFooterButtons
          id={BILLING_PERIODS_TOP_LEVEL_ID}
          isPrimaryButtonDisabled={isPrimaryButtonDisabled}
          onPrimaryButtonClick={
            isEditMode ? handleUpdateBillingPeriod : handleCreateBillingPeriod
          }
          onSecondaryButtonClick={closeAccordion}
          primaryButtonTittle="Save changes"
          primaryIconName={FAIconName.checkRegular}
          secondaryButtonTitle="Cancel"
          secondaryIconName={FAIconName.XmarkRegular}
        />
      )}
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        isPrimaryButtonDisabled={isDeleteBillingPeriodLoading}
        onConfirmDelete={handleDeleteBillingPeriod}
        setIsOpen={setIsDeleteConfirmationModalOpen}
      />
    </>
  );
};

export default BillingPeriodAccordionFooter;
